import React from 'react';
import { Button, HoverStyles, ThreeDotsLoader } from 'wix-ui-tpa/cssVars';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useTranslation, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { useSettingsParams } from '../../../../../hooks';
import { useShouldUseMobileSettings } from '../../../../../hooks/useShouldUseMobileSettings';
import { ButtonStyle } from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import { st, classes } from './Cta.st.css';

interface Props {
  plan: PublicPlan;
  highlighted: boolean;
  buttonDemo: ButtonDemo;
  loading: boolean;
  onClick: () => void;
}

enum ButtonStyleStylableFlag {
  emptyCornered = 'emptyCornered',
  emptyRounded = 'emptyRounded',
  fullCornered = 'fullCornered',
  fullRounded = 'fullRounded',
}

export const Cta: React.FC<Props> = ({ highlighted, buttonDemo, plan, onClick, loading }) => {
  const { experiments } = useExperiments();
  const isHoverSettingsEditorEnabled = experiments.enabled(TPA_EXPERIMENTS.BUTTON_HOVER_SETTINGS_EDITOR);
  const isHoverSettingsViewerEnabled = experiments.enabled(TPA_EXPERIMENTS.BUTTON_HOVER_SETTINGS_VIEWER);
  const settingsParams = useSettingsParams();
  const { isEditor } = useEnvironment();
  const settings = useSettings();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const styles = useStyles();
  const hasFreeTrial = !!plan.pricing?.freeTrialDays;
  const showButtonHoverDemo = isHoverSettingsEditorEnabled && buttonDemo !== ButtonDemo.NONE && isEditor;

  const useMobileSettings = useShouldUseMobileSettings();

  const renderText = () => {
    if (hasFreeTrial) {
      return settings.get(settingsParams.freeTrialButtonText) || t('pp.defaults.freeTrialButton');
    }

    return settings.get(settingsParams.buttonText) || t('pp.defaults.button');
  };

  return (
    <Button
      data-hook={VIEWER_HOOKS.PLAN_CTA}
      hoverStyle={HoverStyles.underline}
      onClick={onClick}
      disabled={experiments.enabled(TPA_EXPERIMENTS.IMPROVE_ECOM_LOADING_STATE) && loading}
      className={st(classes.root, {
        hoverDemo: showButtonHoverDemo,
        hoverSettingsEnabled: isHoverSettingsViewerEnabled,
        highlighted,
        loading,
        mobile: isMobile,
        useMobileSettings,
        style: buttonStyleToStylableFlag(styles.get(stylesParams.buttonStyle)),
      })}
    >
      <span className={classes.label}>{renderText()}</span>
      {loading && (
        <div className={classes.loaderContainer}>
          <ThreeDotsLoader data-hook={VIEWER_HOOKS.PLAN_CTA_LOADER} className={classes.loader} />
        </div>
      )}
    </Button>
  );
};

function buttonStyleToStylableFlag(buttonStyle: ButtonStyle): ButtonStyleStylableFlag {
  switch (buttonStyle) {
    case ButtonStyle.EMPTY_CORNERED:
      return ButtonStyleStylableFlag.emptyCornered;
    case ButtonStyle.EMPTY_ROUNDED:
      return ButtonStyleStylableFlag.emptyRounded;
    case ButtonStyle.FULL_ROUNDED:
      return ButtonStyleStylableFlag.fullRounded;
    default:
    case ButtonStyle.FULL_CORNERED:
      return ButtonStyleStylableFlag.fullCornered;
  }
}
